import type { AxiosApiResponsePromise } from "~/api/apiResponseTypes.ts";
import axiosApi from "~/api/axiosApi.ts";
import type {
	AttachInvoicesToQuotaData,
	CreateQuotaData,
	EligibleQuotaInvoice,
	IndexAvailableQuotaConsumersByCompanyResponse,
	Quota,
	QuotaConsumer,
	QuotaWithRelations,
	SyncQuotaConsumersData,
	UpdateQuotaDetailsData,
} from "~/modules/quota/api/quota/quotaTypes.ts";

const quotasEndpoint = "quotas";

// ADR 0007: a quota is owned by a company (the customer), not a client. The whole quota surface
// scopes by companyId.
function companiesQuotasEndpoint(companyId: string) {
	return `companies/${companyId}/quotas`;
}

function companiesQuotaDetailsEndpoint(companyId: string, quotaId: string) {
	return `${companiesQuotasEndpoint(companyId)}/${quotaId}`;
}

export function indexQuotas(): AxiosApiResponsePromise<QuotaWithRelations[]> {
	return axiosApi.get(quotasEndpoint);
}

export function showQuota(companyId: string,
	quotaId: string,
	withRelations?: boolean): AxiosApiResponsePromise<Quota | QuotaWithRelations> {
	const params = withRelations ? { with_relations: 1 } : undefined;
	return axiosApi.get(companiesQuotaDetailsEndpoint(companyId, quotaId), { params });
}

export function indexQuotasByCompanyId(companyId: string): AxiosApiResponsePromise<QuotaWithRelations[]> {
	return axiosApi.get(companiesQuotasEndpoint(companyId));
}

export function createQuota(companyId: string, data: CreateQuotaData): AxiosApiResponsePromise<Quota> {
	return axiosApi.post(companiesQuotasEndpoint(companyId), { ...data });
}

export function updateQuotaStatus(companyId: string,
	quotaId: string,
	isBookable: boolean): AxiosApiResponsePromise<void> {
	return axiosApi.patch(`${companiesQuotaDetailsEndpoint(companyId, quotaId)}/status`, { isBookable });
}

export function updateQuotaDetails(companyId: string,
	quotaId: string,
	data: UpdateQuotaDetailsData): AxiosApiResponsePromise<void> {
	return axiosApi.patch(`${companiesQuotaDetailsEndpoint(companyId, quotaId)}/details`, { ...data });
}

export function deleteQuota(companyId: string, quotaId: string): AxiosApiResponsePromise<void> {
	return axiosApi.delete(`${companiesQuotasEndpoint(companyId)}/${quotaId}`);
}

export function indexAvailableQuotaConsumersByCompany(companyId: string): AxiosApiResponsePromise<IndexAvailableQuotaConsumersByCompanyResponse> {
	return axiosApi.get(`${companiesQuotasEndpoint(companyId)}/available-consumers`);
}

export function indexEligibleQuotaInvoices(companyId: string): AxiosApiResponsePromise<EligibleQuotaInvoice[]> {
	return axiosApi.get(`${companiesQuotasEndpoint(companyId)}/eligible-invoices`);
}

export function indexQuotaConsumers(companyId: string,
	quotaId: string): AxiosApiResponsePromise<QuotaConsumer[]> {
	return axiosApi.get(`${companiesQuotaDetailsEndpoint(companyId, quotaId)}/consumers`);
}

export function syncQuotaConsumers(companyId: string,
	quotaId: string,
	data: SyncQuotaConsumersData): AxiosApiResponsePromise<void> {
	return axiosApi.put(`${companiesQuotaDetailsEndpoint(companyId, quotaId)}/consumers`, { ...data });
}

export function attachInvoicesToQuota(companyId: string,
	quotaId: string,
	data: AttachInvoicesToQuotaData): AxiosApiResponsePromise<QuotaWithRelations> {
	return axiosApi.post(`${companiesQuotaDetailsEndpoint(companyId, quotaId)}/invoices`, { ...data });
}

// The invoice stays. It only leaves the quota.
export function detachInvoiceFromQuota(companyId: string,
	quotaId: string,
	invoiceId: string): AxiosApiResponsePromise<QuotaWithRelations> {
	return axiosApi.delete(`${companiesQuotaDetailsEndpoint(companyId, quotaId)}/invoices/${invoiceId}`);
}
